import { FormValidationStatus } from '@customTypes/FormValidationStatus'
import { useSlots } from '@lib/hooks/useSlots'
import { CountryCode, formatIncompletePhoneNumber } from 'libphonenumber-js/max'
import React, {
  useImperativeHandle,
  useRef,
  type PropsWithChildren,
} from 'react'
import s from './PhoneNumberInput.module.scss'
import _PhoneCountrySelect from './_PhoneCountrySelect'

interface Props {
  placeholder?: string
  validationStatus?: FormValidationStatus
}
type NativeAttrs = Omit<React.ComponentPropsWithoutRef<'input'>, keyof Props>

export type PhoneNumberInputProps = Props & NativeAttrs

const PhoneNumberInput = React.forwardRef<
  HTMLInputElement,
  PropsWithChildren<PhoneNumberInputProps>
>(
  (
    {
      className,
      placeholder,
      validationStatus,
      disabled,
      required,
      children,
      value,
      onChange: onChangeProp,
      ...props
    },
    forwardedRef,
  ) => {
    const [slots, childrenWithoutSlots] = useSlots(children, {
      countrySelect: _PhoneCountrySelect,
    })
    // Handle ref forwarding
    const ref = useRef<HTMLInputElement>(null)
    useImperativeHandle(forwardedRef, () => ref.current)
    const phoneCountryProps = slots.countrySelect?.props

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      let formattedPhoneNumber = formatIncompletePhoneNumber(
        e.target.value,
        phoneCountryProps.value as CountryCode,
      )
      if (formattedPhoneNumber === value) {
        const digits = formattedPhoneNumber.replace(/\D/g, '')
        const withoutLastDigit = digits.slice(0, digits.length - 1) //remove the last digit
        formattedPhoneNumber = formatIncompletePhoneNumber(
          withoutLastDigit,
          phoneCountryProps.value as CountryCode,
        )
      }
      e.target.value = formattedPhoneNumber
      if (onChangeProp) {
        onChangeProp(e)
      }
    }
    return (
      <div className={s.root}>
        {slots.countrySelect}
        <input
          className={s.input}
          ref={ref}
          aria-invalid={validationStatus === 'error' ? 'true' : undefined}
          type="tel"
          value={value}
          onChange={handleChange}
          {...props}
        />
      </div>
    )
  },
)

PhoneNumberInput.displayName = 'PhoneNumberInput'
export default Object.assign(PhoneNumberInput, {
  CountrySelect: _PhoneCountrySelect,
})
